import { BreakpointObserver } from '@angular/cdk/layout';
import { HttpClient } from '@angular/common/http';
import {
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Inject,
    Input,
    NgZone,
    Output,
    ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LoaderService } from '@icc/b2b/sales-document';
import {
    APP_VERSION,
    drawerAnimation,
    PersonsService,
    scrollTop,
    ComplaintV1,
    ComplaintV1ColumnKey,
    ComplaintV1ColumnSortOrder,
    ComplaintV1Filters,
    complaintV1ListColumns,
} from '@icc/b2b/shared';
import { ConfigService } from '@icc/shared/config.service';
import { core, parseDate } from '@icc/shared/helpers';
import { StatsService } from '@icc/shared/helpers/b2b';
import { TranslateService } from '@ngx-translate/core';
import { ButtonComponent } from 'libs/ui/src/lib/components/button/button.component';
import { NgxPermissionsService } from 'ngx-permissions';
import { ReplaySubject, Subscription } from 'rxjs';

@Component({
    selector: 'icc-complaints-v1-list',
    templateUrl: './complaints-v1-list.component.html',
    styleUrls: ['./complaints-v1-list.component.scss'],
    animations: [drawerAnimation],
})
export class ComplaintsV1ListComponent {
    static crumbsSubject = new ReplaySubject<Record<string, any>>();
    drawerAnimationState: 'fadeIn' | 'fadeOut' = 'fadeIn';
    @ViewChild('drawer', { static: false }) drawer: ElementRef;
    @ViewChild('drawerButton', { static: false, read: ElementRef }) drawerButton: ElementRef;

    allComplaints: ComplaintV1[] = [];

    filters: ComplaintV1Filters;
    columnSortOptions: Partial<
        Record<
            ComplaintV1ColumnKey,
            {
                order: 'asc' | 'desc' | null;
                index: number;
            } | null
        >
    > = {};
    columnsSortOrder: ComplaintV1ColumnSortOrder = [];

    @Input() documentsInPage = 20;
    totalItems = 0;

    private _page = 1;
    @Input()
    get page(): number {
        return this._page;
    }
    set page(page: number) {
        this._page = page;
    }

    @Output() pageEmitter = new EventEmitter();

    subscriptions: Subscription[] = [];

    hasAccessTo = {
        createComplaint: false,
        manageComplaint: false,
    };

    mobileView = false;

    constructor(
        private router: Router,
        private configService: ConfigService,
        private route: ActivatedRoute,
        private translateService: TranslateService,
        private http: HttpClient,
        @Inject(APP_VERSION) private appVersion: string,
        private loaderService: LoaderService,
        private permissionsService: NgxPermissionsService,
        private personsService: PersonsService,
        private breakpointObserver: BreakpointObserver,
        private statsService: StatsService,
        private ngZone: NgZone,
    ) {}

    onChangePage(page: number) {
        this.page = page;
        this.pageEmitter.emit(page);
        this.updateList(true);
        scrollTop();
    }

    @HostListener('window:resize', ['$event'])
    onResize(event) {
        const width = event?.target?.innerWidth ? event.target.innerWidth : event;
        if (this.drawerAnimationState === 'fadeOut' && width > 1200) {
            this.drawerAnimationState = 'fadeIn';
        }
    }

    ngOnInit(): void {
        this.subscriptions.push(
            this.breakpointObserver.observe(['(max-width: 990px)']).subscribe((result) => {
                this.mobileView = result.matches;
                if (result.matches && this.drawerAnimationState === 'fadeIn') {
                    this.drawerAnimationState = 'fadeOut';
                }
            })
        );
        this.onResize(window.innerWidth);
        this.setListCrumbs();
        this.setGeneralAccess();
        this.updateList();
        this.statsService.add({
            action: 'open-complaints-list',
        });
    }

    ngOnDestroy() {
        this.subscriptions.forEach((s) => s.unsubscribe());
    }

    open(document: ComplaintV1) {
        this.router.navigate(['complaint-v1', 'complaint', document.uuid]);
    }

    async updateList(updateRemote = false) {
        await this.getRemoteDocuments();
    }

    async getRemoteDocuments() {
        this.loaderService.setLoadingAfterTime();
        const serverDocuments = await this.http
            .post<{
                complaints: ComplaintV1[];
                limit: number;
                offset: number;
                totalCount: number;
            }>(`${window.location.origin}/api/complaint-v1/complaints`, {
                userId: this.configService?.user?.user?.id,
                appVersion: this.appVersion,
                sort: this.columnsSortOrder || [],
                filters: this.filters || {},
                offset: (this.page - 1) * this.documentsInPage,
                limit: this.documentsInPage,
            })
            .toPromise();
        this.allComplaints = serverDocuments?.complaints.map((doc) => {
            doc.created = parseDate(doc.created);
            return doc;
        }) || [];
        this.totalItems = serverDocuments?.totalCount || 0;
        this.loaderService.setLoaded();
    }

    private setColumnSortDefaults() {
        this.columnsSortOrder = [{ column: 'created', order: 'desc' }];
        this.setColumnSortOptions();
        this.saveConfiguration();
    }

    private setColumnSortOptions() {
        complaintV1ListColumns.forEach((el) => {
            this.columnSortOptions[el] = null;
        });

        this.columnsSortOrder.forEach((el, i) => {
            if (el.column) {
                this.columnSortOptions[el.column] = {
                    index: i + 1,
                    order: el.order,
                };
            }
        });
    }

    orderBy(column: ComplaintV1ColumnKey) {
        const columnOrderIndex = this.columnsSortOrder.findIndex((el) => el.column === column);
        let columnOrder = null;
        if (columnOrderIndex > -1) {
            columnOrder = this.columnsSortOrder[columnOrderIndex];
            this.columnsSortOrder.splice(columnOrderIndex, 1);
        }
        this.columnsSortOrder.unshift({
            column,
            order: columnOrder?.order === 'asc' ? 'desc' : 'asc',
        });
        if (this.columnsSortOrder.length > 3) {
            this.columnsSortOrder.splice(3, this.columnsSortOrder.length - 3);
        }

        this.setColumnSortOptions();
        this.page = 1;
        this.updateList(true);
        this.saveConfiguration();
    }

    saveConfiguration() {
        sessionStorage.setItem(
            `complaints-v1-list-${this.configService?.user?.user?.id}`,
            core.stringJson({
                sort: this.columnsSortOrder,
                page: this.page,
                filters: this.filters,
            })
        );
    }

    loadConfiguration() {
        const { sort, page, filters } = core.parseJson(
            sessionStorage.getItem(`complaints-v1-list-${this.configService?.user?.user?.id}`)
        );
        this.page = page ?? 1;
        if (Array.isArray(sort)) {
            this.columnsSortOrder = sort;
            this.setColumnSortOptions();
        } else {
            this.setColumnSortDefaults();
        }

        if (typeof filters === 'object') {
            this.filters = filters;
        } else {
            this.filters = null;
        }
    }

    trackByUuid(index: number, item: ComplaintV1): string {
        return item.uuid;
    }

    changeDrawerAnimationState() {
        this.drawerAnimationState = 'fadeIn';
    }

    closeDrawer() {
        this.drawerAnimationState = 'fadeOut';
    }

    getDrawerBtnIconName(): 'more_horizontal' | 'more_vertical' {
        return this.drawerAnimationState === 'fadeIn' ? 'more_horizontal' : 'more_vertical';
    }

    setFilters({ filters, reset }: { filters: ComplaintV1Filters; reset: boolean }) {
        this.filters = filters;
        if (reset) {
            this.setColumnSortDefaults();
        }
        this.page = 1;
        this.updateList(true);
        this.saveConfiguration();
        if (this.mobileView) {
            this.closeDrawer();
        }
    }

    async setGeneralAccess() {
        this.hasAccessTo.createComplaint = await this.permissionsService.hasPermission([
            'trade-request-complaint-v1',
        ]);
        this.hasAccessTo.manageComplaint =
            (await this.permissionsService.hasPermission('trade-complaint-v1-manage')) &&
            this.configService.user.user?.ActiveCompanyLicenseType === 'license';
    }

    setListCrumbs() {
        ComplaintsV1ListComponent.crumbsSubject.next({
            level: 0,
            crumbs: null,
        });

        ComplaintsV1ListComponent.crumbsSubject.next({
            level: 1,
            crumbs: [
                {
                    label: this.translateService.instant(
                        'COMPLAINT|Lista zgłoszeń reklamacyjnych'
                    ),
                    url: '/complaint-v1/complaints',
                },
            ],
        });
    }

    createComplaint() {
        this.router.navigate(['complaint-v1', 'complaint', 'new']);
    }

    @HostListener('document:click', ['$event.target'])
    public onClick(targetElement) {
        if (this.drawerAnimationState === 'fadeIn' && this.mobileView) {
            const clickedInsideDrawer = this.drawer?.nativeElement && (
                targetElement && this.drawer?.nativeElement?.contains(targetElement) || this.drawerButton?.nativeElement?.contains(targetElement)
            );
            if (!clickedInsideDrawer) {
                this.ngZone.run(() => {
                    this.closeDrawer();
                });
            }
        }
    }
}
